/**
 * `/retorno` — onde o provedor devolve o navegador depois do login (H01).
 *
 * ── A mesma URL serve três contextos, e ela precisa saber em qual está ────
 *
 *   1. **Aba principal** (login inicial): troca o código por token e navega
 *      para o destino guardado.
 *   2. **Iframe escondido** (`renovarEmSilencio`): não pode navegar nem trocar
 *      nada — só devolver os parâmetros para a aba que o criou.
 *   3. **Popup** (`reautenticarEmPopup`): idem, devolve para quem abriu.
 *
 * Nos casos 2 e 3 quem troca o código é a aba principal, de propósito: o
 * verificador PKCE está na memória DELA, e mandá-lo para cá seria expor o
 * segredo do fluxo para economizar uma mensagem.
 *
 * A mensagem vai com `location.origin` como alvo, nunca `'*'`. Com `'*'`,
 * qualquer página que tivesse aberto esta janela receberia o código de
 * autorização — que é exatamente o que o PKCE existe para proteger.
 */

import { useEffect, useState } from 'react';
import { estaEmbutido } from '@/shared/sessao/embutido';
import { concluirRetorno } from '@/shared/sessao/fluxo-oidc';
import { revendaAtual } from '@/shared/sessao/revenda';
import { Entrada, SUPORTE } from '@/widgets/entrada';

export function Retorno({
  aoConcluir,
}: {
  aoConcluir: (resultado: { destino: string; revenda: string | null }) => void;
}): React.ReactNode {
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    const busca = location.search;
    if (estaEmbutido()) {
      const destinatario = window.opener ?? window.parent;
      (destinatario as Window).postMessage(
        { tipo: 'cliqcrm:retorno-oidc', busca },
        location.origin,
      );
      return;
    }
    void (async () => {
      try {
        aoConcluir(await concluirRetorno(new URLSearchParams(busca)));
      } catch (falha: unknown) {
        definirErro(falha instanceof Error ? falha.message : String(falha));
      }
    })();
  }, [aoConcluir]);

  const revenda = revendaAtual() ?? undefined;

  // Falhou a volta: o código não conferiu, o provedor não trocou. Para quem
  // está entrando é "o login está fora do ar", com o detalhe técnico embaixo,
  // e o caminho é recomeçar do zero — o código de autorização é de uso único.
  if (erro) {
    return (
      <Entrada
        situacao="provedor-fora"
        suporte={SUPORTE}
        revenda={revenda}
        detalhe={erro}
        aoTentarDeNovo={() => location.assign('/')}
      />
    );
  }

  return (
    <Entrada
      situacao="voltando"
      suporte={SUPORTE}
      revenda={revenda}
      aoTentarDeNovo={() => location.assign('/')}
    />
  );
}
